import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { rotuloCurtoMes, temaGrafico } from "./analiseUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/** Valor (curto) escrito acima de cada barra. */
const valorNoTopo = {
    id: "valorNoTopoBarrasMes",
    afterDatasetsDraw(chart, args, opcoes) {
        if (!opcoes.ativo) return;
        const { ctx } = chart;
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.fillStyle = opcoes.cor;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) return;
            meta.data.forEach((barra, i) => {
                const valor = dataset.data[i];
                if (!valor) return;
                const { x, y } = barra.getProps(["x", "y"], true);
                ctx.fillText(opcoes.formatarCurto(valor), x, y - 4);
            });
        });
        ctx.restore();
    },
};

/**
 * Barras agrupadas mês a mês. As séries precisam ter a mesma unidade (um eixo só).
 * series: [{ rotulo, dados: [por mês], cor }]; formatar (tooltip) e formatarCurto (eixo e topo das barras).
 * O valor no topo das barras só aparece com até 4 meses: com mais, os rótulos se encavalam (fica o tooltip).
 */
export default function GraficoBarrasMes({ meses, series, formatar, formatarCurto }) {
    const rotulosNoTopo = meses.length <= 4;
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);

    const maximo = Math.max(...series.flatMap(s => s.dados), 1);

    const data = {
        labels: meses.map(rotuloCurtoMes),
        datasets: series.map(s => ({
            label: s.rotulo,
            data: s.dados,
            backgroundColor: s.cor,
            borderColor: tema.superficie,
            borderWidth: { left: 1, right: 1 },
            borderRadius: { topLeft: 4, topRight: 4 },
            borderSkipped: "bottom",
            barPercentage: 0.8,
            categoryPercentage: 0.6,
            maxBarThickness: 42,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 8 } },
        plugins: {
            legend: {
                position: "top",
                align: "center",
                labels: { color: tema.texto, usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 16, font: { size: 12 } },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatar(ctx.parsed.y)}`,
                },
            },
            valorNoTopoBarrasMes: { cor: tema.texto, formatarCurto, ativo: rotulosNoTopo },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: tema.grade },
                ticks: { color: tema.textoSecundario, font: { size: 12 } },
            },
            y: {
                beginAtZero: true,
                suggestedMax: rotulosNoTopo ? maximo * 1.15 : undefined, // espaço para o valor no topo das barras
                grid: { color: tema.grade },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, callback: (v) => formatarCurto(v) },
            },
        },
    };

    return (
        <MDBox height="290px">
            <Bar data={data} options={options} plugins={[valorNoTopo]} />
        </MDBox>
    );
}
